import React, { useState, useEffect } from 'react';
import { useClient } from '../../contexts/ClientContext';
import { useToast } from '../../hooks/use-toast';
import { getApiUrl } from '../../utils/config';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Clock, 
  Mail, 
  X, 
  Calendar,
  User,
  Crown,
  Shield,
  Edit3,
  Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  message?: string;
  expires_at: string;
  created_at: string;
  inviter: {
    username: string;
  };
}

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'owner': return <div className="w-4 h-4" />;
    case 'admin': return <Shield className="w-4 h-4" />;
    case 'editor': return <Edit3 className="w-4 h-4" />;
    case 'member': return <User className="w-4 h-4" />;
    case 'viewer': return <Eye className="w-4 h-4" />;
    default: return <User className="w-4 h-4" />;
  }
};

const getRoleColor = (role: string) => {
  switch (role) {
    case 'owner': return 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 dark:from-yellow-900/30 dark:to-amber-900/30 dark:text-yellow-300';
    case 'admin': return 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 dark:from-red-900/30 dark:to-rose-900/30 dark:text-red-300';
    case 'editor': return 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 dark:from-blue-900/30 dark:to-indigo-900/30 dark:text-blue-300';
    case 'member': return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-300';
    case 'viewer': return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 dark:from-gray-900/30 dark:to-slate-900/30 dark:text-gray-300';
    default: return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 dark:from-gray-900/30 dark:to-slate-900/30 dark:text-gray-300';
  }
};

interface PendingInvitationsCardProps {
  onInvitationChange: () => void;
}

export const PendingInvitationsCard: React.FC<PendingInvitationsCardProps> = ({ onInvitationChange }) => {
  const { currentClient } = useClient();
  const { toast } = useToast();
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentClient) {
      fetchInvitations();
    }
  }, [currentClient]);

  const fetchInvitations = async () => {
    if (!currentClient) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl()}/api/v1/clients/${currentClient.id}/team/invitations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      } else {
        console.error('Failed to fetch invitations');
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    if (!currentClient) return;
    
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/clients/${currentClient.id}/team/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });
      
      if (response.ok) {
        toast({
          title: "Success",
          description: "Invitation cancelled successfully",
        });
        fetchInvitations();
        onInvitationChange();
      } else {
        throw new Error('Failed to cancel invitation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel invitation",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mb-4"></div>
        <div className="space-y-3">
          <div className="h-16 bg-slate-200 dark:bg-slate-700 rounded"></div>
          <div className="h-16 bg-slate-200 dark:bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <motion.div 
      className="relative overflow-hidden rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/30 dark:from-orange-500/5 dark:via-amber-500/5 dark:to-slate-500/5"></div>
      <div className="relative p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center space-x-2">
            <Clock className="w-5 h-5 text-orange-500" />
            <span>Pending Invitations</span>
          </h3>
          <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
            {invitations.length} pending
          </Badge>
        </div>
        
        <div className="space-y-3">
          <AnimatePresence>
            {invitations.map((invitation, index) => (
              <motion.div 
                key={invitation.id}
                className="group relative overflow-hidden rounded-xl bg-white/60 dark:bg-slate-700/40 backdrop-blur-sm border border-slate-200/40 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-700/60 transition-all duration-300"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/50 dark:to-amber-900/50 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="font-medium text-slate-800 dark:text-slate-100 truncate">
                            {invitation.email}
                          </p>
                          <Badge className={`${getRoleColor(invitation.role)} border-0 shadow-sm`}>
                            <div className="flex items-center space-x-1">
                              {getRoleIcon(invitation.role)}
                              <span className="capitalize font-medium">{invitation.role}</span>
                            </div>
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-slate-600 dark:text-slate-400">
                          <span>Invited by {invitation.inviter.username}</span>
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>Expires {new Date(invitation.expires_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        {invitation.message && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 italic">
                            "{invitation.message}"
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelInvitation(invitation.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};